<template>
  <section
    v-if="visible"
    id="plan-quality-review"
    style="margin: 1rem 0;">
    <a-divider>
      Проверка качества
      <a-tag
        v-if="!loading"
        :style="{ marginLeft: '8px' }">
        {{ issues.length }}
      </a-tag>
    </a-divider>

    <a-skeleton
      v-if="loading"
      active
      :paragraph="{ rows: 4 }" />

    <a-collapse
      v-else
      allow-multiple
      style="width: 100%;">
      <a-collapse-panel
        v-for="group in groups"
        :key="group.key"
        :header="group.title"
        style="width: 100%;">
        <template #extra>
          <a-space>
            <a-tag :color="group.verdictColor">
              {{ group.verdictLabel }}
            </a-tag>
            <a-tag>
              замечаний: {{ group.issues.length }}
            </a-tag>
          </a-space>
        </template>

        <div class="qa-log__meta">
          <span>Проверяющий: {{ group.reviewer || '—' }}</span>
          <span v-if="group.assignee">Исполнитель: {{ group.assignee }}</span>
          <span v-if="group.period">{{ group.period }}</span>
        </div>

        <a-empty
          v-if="!group.issues.length"
          description="В этой проверке замечаний не было" />

        <div
          v-for="issue in group.issues"
          :key="issue.id"
          class="qa-log__issue">
          <div class="qa-log__issue-head">
            <span class="qa-log__title">{{ issue.title }}</span>
            <a-tag :color="kindColor(issue.kind)">
              {{ kindLabel(issue.kind) }}
            </a-tag>
            <a-tag
              v-if="stageLabel(issue)"
              color="geekblue">
              {{ stageLabel(issue) }}
            </a-tag>
            <a-tag
              v-if="floorLabel(issue)"
              color="blue">
              {{ floorLabel(issue) }}
            </a-tag>
            <a-tag
              v-if="issue.category"
              color="purple">
              {{ issue.category.name }}
            </a-tag>
            <a-tag :color="statusColor(issue.status)">
              {{ statusLabel(issue.status) }}
            </a-tag>
          </div>

          <a-timeline class="qa-log__timeline">
            <a-timeline-item
              v-for="(event, index) in issueEvents(issue)"
              :key="index"
              :color="event.color">
              <div class="qa-log__event-head">
                <strong>{{ event.side }}</strong>
                <span
                  v-if="event.author"
                  class="qa-log__muted">
                  {{ event.author }}
                </span>
                <span
                  v-if="event.at"
                  class="qa-log__muted">
                  {{ event.at }}
                </span>
              </div>
              <div class="qa-log__event-action">
                {{ event.action }}
              </div>
              <rich-text-content
                v-if="event.comment"
                class="qa-log__event-comment"
                :value="event.comment" />
              <image-viewer
                v-if="event.screenshot"
                :images="[event.screenshot]">
                <img
                  class="qa-log__shot"
                  :src="event.screenshot"
                  :alt="event.side">
              </image-viewer>
            </a-timeline-item>
          </a-timeline>
        </div>
      </a-collapse-panel>
    </a-collapse>
  </section>
</template>

<script setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import moment from 'moment';
import { api } from '@/api';
import getFlag from '@/helpers/getFlag';
import ImageViewer from '@/Components/Common/ImageViewer.vue';

const props = defineProps({
  crmPlanId: { type: Number, required: true },
  type: { type: Number, required: true },
});

const enabled = getFlag('isQualityReviewPanelsVisible');
const issues = ref([]);
const reviews = ref([]);
const loading = ref(false);

const visible = computed(() => enabled && (issues.value.length > 0 || reviews.value.length > 0));

const formatDate = (value) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : null);

const KIND_LABELS = {
  error: 'Замечание',
  recommendation: 'Рекомендация',
  widget_bug: 'Баг клиентского виджета',
};
const KIND_COLORS = { error: 'red', recommendation: 'gold', widget_bug: 'magenta' };
const STATUS_LABELS = {
  open: 'Открыто',
  fixed: 'Исправлено',
  couldnt_fix: 'Не получилось',
  confirmed: 'Подтверждено',
  reopened: 'Возвращено в работу',
};
const STATUS_COLORS = {
  open: 'red',
  fixed: 'green',
  couldnt_fix: 'volcano',
  confirmed: 'green',
  reopened: 'orange',
};
const STAGE_LABELS = {
  jpg: 'JPG', svg: 'SVG', '2d': '2D', '3d': '3D', tour: '360',
};

const kindLabel = (kind) => KIND_LABELS[kind] ?? kind;
const kindColor = (kind) => KIND_COLORS[kind] ?? 'default';
const statusLabel = (status) => STATUS_LABELS[status] ?? status;
const statusColor = (status) => STATUS_COLORS[status] ?? 'default';
const stageLabel = (issue) => STAGE_LABELS[issue.anchor?.stage] ?? null;
const floorLabel = (issue) => {
  const anchor = issue.anchor ?? {};
  if (anchor.floor_index === undefined || anchor.floor_index === null) return null;
  if (Number(anchor.floors_total ?? 0) < 2) return null;
  return `Этаж ${Number(anchor.floor_index) + 1}`;
};

// Переписка по замечанию лежит в comments и хранит все круги правок,
// поэтому лента строится по ней, а статусы и скриншоты добавляются сверху.
const RESOLVED_ACTIONS = {
  couldnt_fix: 'Не смог исправить',
  fixed: 'Отметил исправленным',
};

const issueEvents = (issue) => {
  const history = issue.comments ?? [];
  const opening = history.find((item) => item.role === 'issue') ?? null;
  const replies = history.filter((item) => item.role !== 'issue');
  const lastReply = replies[replies.length - 1] ?? null;
  // Скриншот «После» у замечания один — он относится к последнему ответу исполнителя.
  const lastResolution = [...replies].reverse().find((item) => item.role === 'resolution') ?? null;

  const events = [{
    side: 'Проверяющий',
    author: opening?.author?.name ?? issue.author?.name ?? null,
    at: formatDate(opening?.created_at ?? issue.created_at),
    action: `Поставил${kindLabel(issue.kind) === 'Рекомендация' ? ' рекомендацию' : ' замечание'}`,
    comment: opening?.text ?? null,
    screenshot: issue.before_screenshot,
    color: 'red',
  }];

  replies.forEach((item) => {
    const isReopen = item.role === 'reopen';
    // Роль ответа исполнителя не хранится отдельно: «исправил» это было или «не получилось»,
    // достоверно известно только для последнего — по текущему статусу замечания.
    const resolvedAction = item === lastReply ? RESOLVED_ACTIONS[issue.status] : null;

    events.push({
      side: isReopen ? 'Проверяющий' : 'Исполнитель',
      author: item.author?.name ?? null,
      at: formatDate(item.created_at),
      action: isReopen ? 'Не подтвердил, вернул в работу' : (resolvedAction ?? 'Ответил по замечанию'),
      comment: item.text,
      screenshot: item === lastResolution ? issue.after_screenshot : null,
      color: isReopen ? 'orange' : 'green',
    });
  });

  // Ниндзя мог отчитаться без текста — тогда в переписке ничего нет, а результат есть.
  const answered = ['fixed', 'couldnt_fix'].includes(issue.status);
  if (answered && (!lastReply || lastReply.role === 'reopen')) {
    events.push({
      side: 'Исполнитель',
      author: issue.resolver?.name ?? null,
      at: formatDate(issue.resolved_at),
      action: RESOLVED_ACTIONS[issue.status],
      comment: null,
      screenshot: issue.after_screenshot,
      color: issue.status === 'couldnt_fix' ? 'orange' : 'green',
    });
  }

  if (issue.status === 'confirmed') {
    events.push({
      side: 'Проверяющий',
      author: issue.author?.name ?? null,
      at: null,
      action: 'Подтвердил исправление',
      comment: null,
      screenshot: null,
      color: 'green',
    });
  }

  return events;
};

const VERDICT_LABELS = { passed: 'Одобрено', failed: 'На доработку' };
const VERDICT_COLORS = { passed: 'green', failed: 'red' };

const groups = computed(() => {
  const byReview = new Map();
  reviews.value.forEach((review) => byReview.set(review.id, { review, issues: [] }));

  issues.value.forEach((issue) => {
    if (!byReview.has(issue.review_id)) {
      byReview.set(issue.review_id, { review: null, issues: [] });
    }
    byReview.get(issue.review_id).issues.push(issue);
  });

  return [...byReview.entries()].map(([reviewId, { review, issues: reviewIssues }]) => {
    const started = formatDate(review?.started_at);
    const submitted = formatDate(review?.submitted_at);

    return {
      key: String(reviewId),
      iteration: review?.iteration ?? reviewIssues[0]?.iteration ?? 0,
      title: `Проверка №${review?.iteration ?? reviewIssues[0]?.iteration ?? '—'}`,
      reviewer: review?.reviewer?.name ?? reviewIssues[0]?.author?.name ?? null,
      assignee: review?.assignee?.name ?? null,
      verdictLabel: VERDICT_LABELS[review?.verdict] ?? 'Идёт проверка',
      verdictColor: VERDICT_COLORS[review?.verdict] ?? 'blue',
      period: [started, submitted].filter(Boolean).join(' — '),
      issues: reviewIssues,
    };
  }).sort((a, b) => b.iteration - a.iteration || Number(b.key) - Number(a.key));
});

const load = async () => {
  if (!enabled || !props.crmPlanId) return;

  loading.value = true;
  try {
    const [issuesResponse, reviewsResponse] = await Promise.all([
      api.getPlanIssues({ planId: props.crmPlanId, type: props.type }),
      api.getPlanReviews({ planId: props.crmPlanId, type: props.type }),
    ]);
    const unwrap = (data) => (Array.isArray(data) ? data : (data?.data ?? []));
    issues.value = unwrap(issuesResponse.data);
    reviews.value = unwrap(reviewsResponse.data);
  } catch (e) {
    // Нет доступа или проверок ещё не было — блок просто не показываем.
    issues.value = [];
    reviews.value = [];
  } finally {
    loading.value = false;
  }
};

watch(() => [props.crmPlanId, props.type], load);
onMounted(load);

defineExpose({ reload: load });
</script>

<style scoped>
.qa-log__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.65);
}
.qa-log__muted {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}
.qa-log__issue {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fff;
}
.qa-log__issue-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.qa-log__title {
  font-weight: 500;
}
.qa-log__timeline {
  margin: 0;
  padding-top: 4px;
}
.qa-log__event-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.qa-log__event-action {
  color: rgba(0, 0, 0, 0.85);
}
.qa-log__event-comment {
  color: rgba(0, 0, 0, 0.65);
  white-space: pre-line;
}
.qa-log__shot {
  display: block;
  max-width: 320px;
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  cursor: zoom-in;
}
</style>
